import moment from "moment-timezone";
import Decimal from "decimal.js";
import { useState, useMemo } from "react";
import { AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BirthdayFundRequest, ListRequestsResponse } from "@/lib/api/fund";
import { CommonDestinationTimezone, formatCurrency, cn } from "@/lib/utils";
import { formatNumberToDisplay } from "@/lib/api/common";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { sum, sumDecimal } from "@/components/ui/DataTable/aggregation/index";
import { ResponsivePie } from "@nivo/pie";

interface BirthdayFundSummary {
  total_requested: number;
  total_granted: number;
  amount_granted: string;
}

interface StatsCardProps {
  title: string;
  value: string;
  description: string | string[];
  className?: string;
  isCurrency?: boolean;
}

interface OrganizationSummaryRowProps {
  organization: string;
  summary: BirthdayFundSummary;
}

interface MonthSummaryRowProps {
  month: string;
  summary: BirthdayFundSummary;
}

export function StatsCard({ title, value, description, className = "", isCurrency = false }: StatsCardProps) {
  return (
    <Card className={`p-6 ${className}`}>
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      <p className="text-3xl font-bold mt-2">{value}</p>
      {(description && Array.isArray(description)) && (
        description.map((item: string, index: number) => <p key={`stats_${title}_${index}`} className="text-sm text-gray-500 mt-1">{item}</p>)
      )}
      {(description && !Array.isArray(description)) && (<p className="text-sm text-gray-500 mt-1">{description}</p>)}
      
    </Card>
  );
}

export function OrganizationSummaryRow({ organization, summary }: OrganizationSummaryRowProps) {
  const completionPercentage = summary.total_requested > 0
    ? (summary.total_granted / summary.total_requested) * 100
    : 0;

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center space-x-2">
          <h4 className="font-medium">{organization}</h4>
          <Badge variant="outline">{summary.total_granted}/{summary.total_requested}</Badge>
        </div>
        <span className="font-semibold">{formatCurrency(summary.amount_granted)}</span>
      </div>

    </div>
  );
}


export function MonthSummaryRow({ month, summary }: MonthSummaryRowProps) {
  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center space-x-2">
          <h4 className="font-medium">{moment.tz(month, "YYYY-MM-DD", CommonDestinationTimezone).format("MMM Do YYYY")}</h4>
          <Badge variant="outline">{summary.total_granted}/{summary.total_requested}</Badge>
        </div>
        <span className="font-semibold">{formatCurrency(summary.amount_granted)}</span>
      </div>

    </div>
  );
}

